import type { Anim, PlaceId } from '../../../shared/ids';
import { isPlaceId } from '../../../shared/ids';
import { NAME_MAX } from '../../../shared/limits';
import type { MeetKind, PlayerCard, PosFields, Pub } from '../../../shared/protocol';
import { placeBounds } from '../../../shared/world/rooms';

// 같은 초대 방에 있는 다른 사람들. 서버에서 받은 사본이고, 값을 바꾸는 함수는 이 파일에만 있다(3-store).
export type Peer = {
  id: string; pid?: string; name: string; look: number;
  room: PlaceId | null;                             // 마을·건물·모험 스테이지(`stage:1-1`). null = 어디에도 없다(지도·제목 화면이거나 아직 안 들어옴)
  x: number; y: number; face: 1 | -1; st: Anim;     // 그리는 위치(앞질러 그린 값)
  seat: number; sh: number; hd: number;             // 앉은 자리 번호(-1 서 있음)·화면 공유·손들기. 앞 둘의 뜻은 meeting이 안다
  gr: number; hdAt: number;                         // 발언권(강사가 줬다)·손을 든 때(손 든 순서)
  pub: Pub | null;                                  // 서버 송출(SFU)로 올리는 트랙 공지 (학교)
  bx: number; by: number; vx: number; vy: number; bt: number; ex: number; ey: number;   // 마지막으로 받은 점·속도·받은 시각, 남은 어긋남(서서히 줄인다)
  t: number; runT: number; blinkT: number;          // 그림 시계: 숨쉬기·걷기·눈 깜박임
};

export const peers = new Map<string, Peer>();     // 서버 접속 id → 사람
export const LINK: { state: string; id: string | null } = { state: 'idle', id: null };   // idle | connecting | online | offline | full
const SNAP_PX = 40;   // 이만큼 어긋나면 보정 없이 바로 옮긴다 (대시 한 번이 16px)

const clampTo = (room: PlaceId | null, x: number, y: number): [number, number] => {
  const b = room ? placeBounds(room) : null;
  return b ? [Math.min(Math.max(+x || 0, b.x0), b.x1), Math.min(Math.max(+y || 0, b.y0), b.y1)] : [0, 0];
};

export function addPeer(p: PlayerCard) {
  const room = isPlaceId(p.room) ? p.room : null, [x, y] = clampTo(room, p.x, p.y);
  peers.set(p.id, {
    id: p.id, pid: typeof p.pid === 'string' ? p.pid : undefined, name: String(p.name || '손님').slice(0, NAME_MAX), look: p.look | 0, room, x, y, face: p.face === -1 ? -1 : 1, st: p.st,
    seat: typeof p.seat === 'number' ? p.seat : -1, sh: p.sh | 0, hd: p.hd | 0, gr: p.gr ? 1 : 0, hdAt: p.hd ? performance.now() : 0, pub: p.pub && typeof p.pub.sid === 'string' ? p.pub : null, bx: x, by: y, vx: 0, vy: 0, bt: performance.now(), ex: 0, ey: 0, t: 0, runT: 0, blinkT: 2,
  });
}
export function pushPos(id: string, m: PosFields) {
  const r = peers.get(id);
  if (!r || !isPlaceId(m.room)) return;
  const [x, y] = clampTo(m.room, m.x, m.y), now = performance.now();
  // 받은 점에서 새로 예측을 시작한다. 지금 그려 둔 위치와 새 예측의 차이는 남겨 두고 서서히 줄인다
  const snap = r.room !== m.room || Math.hypot(r.x - x, r.y - y) > SNAP_PX;
  r.ex = snap ? 0 : r.x - x; r.ey = snap ? 0 : r.y - y;
  r.room = m.room; r.bx = x; r.by = y; r.vx = m.vx | 0; r.vy = m.vy | 0; r.bt = now; r.face = m.face === -1 ? -1 : 1; r.st = m.st;
  if (snap) { r.x = x; r.y = y; }
}
// 그 사람이 장소를 떠났다: 자리·손들기도 풀린다
export function setAway(id: string) { const r = peers.get(id); if (r) { r.room = null; r.seat = -1; r.hd = 0; r.sh = 0; r.gr = 0; r.pub = null; r.st = 'idle'; } }
export function setPeerMeet(id: string, k: MeetKind, v: number) {
  const r = peers.get(id);
  if (!r) return;
  if (k === 'seat') r.seat = v | 0; else if (k === 'share') r.sh = v ? 1 : 0; else { if (v && !r.hd) r.hdAt = performance.now(); r.hd = v ? 1 : 0; }
}
export function setPeerGrant(id: string, on: boolean) { const r = peers.get(id); if (r) r.gr = on ? 1 : 0; }
export function clearPeerGrants() { for (const r of peers.values()) r.gr = 0; }
export function tickPeer(r: Peer, dt: number) {   // 그림 시계만 간다
  r.t += dt; r.blinkT -= dt;
  if (r.blinkT < -0.12) r.blinkT = 1.6 + Math.random() * 2.4;
  r.runT = r.st === 'walk' ? r.runT + dt : 0;
}
export function setPeerPub(id: string, pub: Pub | null) { const r = peers.get(id); if (r) r.pub = pub && typeof pub.sid === 'string' ? pub : null; }
export function placePeer(r: Peer, x: number, y: number, face?: 1 | -1, st?: Anim) { r.x = x; r.y = y; if (face) r.face = face; if (st) r.st = st; }

// 서버 연결 상태. 연결이 됐는지는 컨트롤러가 여기서 읽는다(웹소켓 자체는 1-endpoints/net이 안다): online은 welcome을 받은 때부터 끊길 때까지
export const isOnline = () => LINK.state === 'online';
export function setLink(state: string, id?: string | null) { LINK.state = state; if (id !== undefined) LINK.id = id; }
export function clearPeers() { peers.clear(); }
export function removePeer(id: string) { peers.delete(id); }
